import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { Order, fetchOrders } from '../api/orderApi';
import { toast } from 'react-toastify';
import { playNotificationSound } from '../utils/playNotificationSound';
import { showNewOrderPopup } from '../components/NewOrderNotification';
import { useAuth } from './useAuth';
import { useOrderNotifications } from '../context/OrderNotificationContext';

const SOCKET_URL =  process.env.REACT_APP_SOCKET_URL || 'https://api.flavourchaser.co';
const LOCAL_KNOWN_KEY_PREFIX = 'fc_known_orders';

export const useOrderSocket = () => {
  const socketRef = useRef<Socket | null>(null);
  const { user } = useAuth();
  const { addUnread, addWholesaleUnread } = useOrderNotifications();

  const knownOrders = useRef<Set<number>>(new Set());
  const notifiedOrders = useRef<Set<number>>(new Set());
  const bootstrapped = useRef(false);

  useEffect(() => {
    if (!user) return;

    // ✅ Make it per-user so different admins don't share state on same machine
    const LOCAL_KNOWN_KEY = `${LOCAL_KNOWN_KEY_PREFIX}:${user.email}`;


    const saved = localStorage.getItem(LOCAL_KNOWN_KEY);
    if (saved) {
      try {
        knownOrders.current = new Set(JSON.parse(saved));
      } catch {
        knownOrders.current = new Set();
      }
    }

    const persistKnownOrders = () => {
      localStorage.setItem(LOCAL_KNOWN_KEY, JSON.stringify(Array.from(knownOrders.current)));
    };

    const socket = io(SOCKET_URL, {
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      // optional if your backend uses auth
      auth: { token: localStorage.getItem('authToken') }
    });

    socketRef.current = socket;

const handleOrder = (data: Order) => {
  // Idempotent per order: ignore a repeat event for an order we've already
  // announced this session (guards against duplicate socket emits).
  if (notifiedOrders.current.has(data.order_id)) return;

  notifiedOrders.current.add(data.order_id);
  knownOrders.current.add(data.order_id);
  persistKnownOrders();

  const soundAllowed = localStorage.getItem("soundPermission") === "granted";
  if (soundAllowed) playNotificationSound();

  toast.info(`New Online Order #${data.order_id}`, {
    autoClose: 5000,
    position: "bottom-right",
  });

  showNewOrderPopup(data);
  addUnread();
};

    // ✅ Seed recent orders WITHOUT firing notifications
    const bootstrapKnownOrders = async () => {
  try {
    const recent = await fetchOrders({ source: 'online', limit: 25 });


    recent.forEach((o) => knownOrders.current.add(o.order_id));
    persistKnownOrders();

    bootstrapped.current = true;
  } catch (err) {
    console.error('Failed to bootstrap known orders:', err);
    bootstrapped.current = true;
  }
};

    socket.on('connect', () => {
      console.log('🟢 Socket connected');
      bootstrapKnownOrders();
    });

    socket.on('newOrder', handleOrder);

    // Distinct wholesale-signup notification → its own badge counter (not orders).
    const notifiedWholesale = new Set<number>();
    const handleWholesaleRequest = (data: {
      request_id: number;
      business_name?: string;
      contact_name?: string;
    }) => {
      if (data?.request_id && notifiedWholesale.has(data.request_id)) return;
      if (data?.request_id) notifiedWholesale.add(data.request_id);
      const soundAllowed = localStorage.getItem('soundPermission') === 'granted';
      if (soundAllowed) playNotificationSound();
      toast.info(
        `New wholesale application — ${data?.business_name || data?.contact_name || 'new customer'}`,
        { autoClose: 6000, position: 'bottom-right' }
      );
      addWholesaleUnread();
    };
    socket.on('newWholesaleRequest', handleWholesaleRequest);

    socket.on('disconnect', () => {
      console.warn('🔌 Disconnected from socket');
      bootstrapped.current = false; // will re-bootstrap on reconnect
    });

    socket.on('connect_error', (err) => {
      console.error('❌ Socket connection error:', err);
    });

    return () => {
      socket.disconnect();
    };
  }, [user, addUnread, addWholesaleUnread]);
};
